import React, { useState } from 'react';
import { hoverHelpers } from 'shared-helpers';
import { amountFormatter } from '/src/lib/helpers';
import { getEntityProfileUrl } from '/src/components/ICESections/Investments/helpers';
import EstimationIndicator from '/src/components/Reusable/EstimationIndicator';
import useLpCommitmentsModal from './useLpCommitmentsModal';
import { getInitials, MOB_BADGE_COLORS } from '/src/components/ICESections/Shared/mobileListUtils';

const LpCommitmentsModal = ({ isOpen, onClose, fundName, lpRows = [], hasDirectoryAccess = false }) => {
    const { contentRef, contentScrollState } = useLpCommitmentsModal({ isOpen, onClose });
    const [expandedRows, setExpandedRows] = useState([]);

    const toggleRow = (key) => setExpandedRows(prev =>
        prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );

    if (!isOpen) return null;

    const totalCommitment = lpRows.reduce((sum, lp) => sum + Number(lp.amount || lp.estimatedAmount || 0), 0);

    const dialogClassName = [
        'lp-commitments-modal__dialog',
        contentScrollState.hasMoreAbove && 'lp-commitments-modal__dialog--scrolled',
    ].filter(Boolean).join(' ');

    return (
        <div
            className="lp-commitments-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lp-commitments-modal-title"
        >
            <button
                type="button"
                className="lp-commitments-modal__backdrop"
                aria-label="Close"
                onClick={onClose}
            />

            <div className={dialogClassName}>
                <div className="lp-commitments-modal__header">
                    <div className="lp-commitments-modal__header-title-row">
                        <h3 id="lp-commitments-modal-title">LP Commitments</h3>
                        <button
                            type="button"
                            className="lp-commitments-modal__close"
                            aria-label="Close"
                            onClick={onClose}
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <EstimationIndicator />

                    <div className="metric-card-row">
                        <div className="metric-card">
                            <span className="metric-card-label">Fund</span>
                            <span className="metric-card-val">{fundName || '—'}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-card-label">Total LPs</span>
                            <span className="metric-card-val">{lpRows.length}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-card-label">Total Commitment Amount</span>
                            <span className="metric-card-val">
                                {totalCommitment > 0 ? amountFormatter(totalCommitment) : 'Undisclosed'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="lp-commitments-modal__table-shell">
                    <div ref={contentRef} className="lp-commitments-modal__content">
                        {lpRows.length === 0 ? (
                            <p className="lp-commitments-modal__empty">No LP data available.</p>
                        ) : (
                            <>
                                {/* ── Desktop table ── */}
                                <table className="lp-commitments-modal__table">
                                    <thead>
                                        <tr>
                                            <th>LP Name</th>
                                            <th>HQ</th>
                                            <th>LP Type</th>
                                            <th>Commitment Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lpRows.map((lp, idx) => (
                                            <tr key={lp.id ?? idx}>
                                                <td>
                                                    {(() => {
                                                        const url = getEntityProfileUrl({ id: lp.id, code: lp.code, type: lp.entityType });
                                                        const hoverProps = {
                                                            onMouseEnter: (e) => hoverHelpers.handleEntityHover({ ...lp, type: lp.entityType }, e, hasDirectoryAccess),
                                                            onMouseLeave: () => hoverHelpers.handleMouseOut(),
                                                        };
                                                        return url
                                                            ? <a href={url} target="_blank" rel="noopener noreferrer" {...hoverProps}>{lp.name || '—'}</a>
                                                            : <span className="lp-commitments-modal__lp-name" {...hoverProps}>{lp.name || <span className="lp-commitments-modal__na">–</span>}</span>;
                                                    })()}
                                                </td>
                                                <td>{lp.hq   || <span className="lp-commitments-modal__na">–</span>}</td>
                                                <td>{lp.type  || <span className="lp-commitments-modal__na">–</span>}</td>
                                                <td>
                                                    {(() => {
                                                        const isEstimated = !lp.amount && lp.estimatedAmount;
                                                        const display = lp.amount || lp.estimatedAmount;
                                                        return display
                                                            ? <span className={isEstimated ? 'amount--estimated' : ''}><strong>{amountFormatter(display)}</strong></span>
                                                            : <span className="lp-commitments-modal__na">Undisclosed</span>;
                                                    })()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* ── Mobile card list ── */}
                                <div className="lp-commitments-modal__mobile-list">
                                    {lpRows.map((lp, idx) => {
                                        const key = lp.id ?? idx;
                                        const isOpen = expandedRows.includes(key);
                                        const col = MOB_BADGE_COLORS[idx % MOB_BADGE_COLORS.length];
                                        const url = getEntityProfileUrl({ id: lp.id, code: lp.code, type: lp.entityType });
                                        const hoverProps = {
                                            onMouseEnter: (e) => hoverHelpers.handleEntityHover({ ...lp, type: lp.entityType }, e, hasDirectoryAccess),
                                            onMouseLeave: () => hoverHelpers.handleMouseOut(),
                                        };
                                        const isEstimated = !lp.amount && lp.estimatedAmount;
                                        const display = lp.amount || lp.estimatedAmount;

                                        return (
                                            <div
                                                key={key}
                                                className="mob-investment-row"
                                                onClick={() => toggleRow(key)}
                                            >
                                                <div className="mob-investment-row__top">
                                                    <div className="mob-badge" style={{ background: col.bg, color: col.txt }}>
                                                        {getInitials(lp.name)}
                                                    </div>
                                                    <div className="mob-investment-row__main">
                                                        {url ? (
                                                            <a
                                                                href={url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="lp-commitments-modal__lp-link"
                                                                onClick={e => e.stopPropagation()}
                                                                {...hoverProps}
                                                            >
                                                                {lp.name || '—'}
                                                            </a>
                                                        ) : (
                                                            <span
                                                                className="lp-commitments-modal__lp-link"
                                                                {...hoverProps}
                                                            >
                                                                {lp.name || '—'}
                                                            </span>
                                                        )}
                                                        <div className="mob-investment-row__meta">
                                                            {lp.hq || '—'}
                                                        </div>
                                                    </div>
                                                    <div className="mob-investment-row__right">
                                                        <span className="mob-investment-row__amount">
                                                            {display
                                                                ? <span className={isEstimated ? 'amount--estimated' : ''}>{amountFormatter(display)}</span>
                                                                : <span className="lp-commitments-modal__na">Undisclosed</span>}
                                                        </span>
                                                        <div className="mob-investment-row__status-row">
                                                            <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} mob-investment-row__chevron`} />
                                                        </div>
                                                    </div>
                                                </div>

                                                {isOpen && (
                                                    <div className="mob-investment-row__expand">
                                                        <div className="mob-expand-grid">
                                                            <div className="mob-expand-cell">
                                                                <div className="mob-expand-cell__lbl">LP Type</div>
                                                                <div className="mob-expand-cell__val">{lp.type || '—'}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>

                    {contentScrollState.hasMoreBelow && (
                        <div
                            className="lp-commitments-modal__scroll-indicator"
                            aria-hidden="true"
                        >
                            <span>Scroll to view more</span>
                            <i className="fas fa-chevron-down"></i>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LpCommitmentsModal;
